import React, { useState } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faPaperPlane, faHeading } from '@fortawesome/free-solid-svg-icons';
import './ContactUs.css';
import { toast, ToastContainer } from 'react-toastify';
import CTAPrimaryButton from '../../Components/CTAButton/CTAPrimaryButton/CTAPrimaryButton';
import { useTranslation } from 'react-i18next';

const ContactUs = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('ContactUs.Form.Validation.FirstNameRequired');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('ContactUs.Form.Validation.EmailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('ContactUs.Form.Validation.EmailInvalid');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('ContactUs.Form.Validation.PhoneRequired');
    } else if (!/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = t('ContactUs.Form.Validation.PhoneInvalid');
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = t('ContactUs.Form.Validation.SubjectRequired');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('ContactUs.Form.Validation.MessageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('ContactUs.Form.Validation.MessageLength');
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Handle form submission here
      console.log(formData);
      toast.success(t('ContactUs.Form.Success'));
      // Reset form after successful submission
      setFormData({
        firstName: '',
        email: '',
        subject: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      toast.error(t('ContactUs.Form.Error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="contact-container">
      <ToastContainer position="top-right" />
      <div className="contact-wrapper">
        <h2 className="contact-title">{t('ContactUs.Title')}</h2>
        <p className="contact-description">
          {t('ContactUs.Description')}
        </p>
        
        <Form onSubmit={handleSubmit} className="contact-form">
          <Row>
            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  {t('ContactUs.Form.FirstName')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  isInvalid={!!errors.firstName}
                  placeholder={t('ContactUs.Form.EnterFirstName')}
                  className="form-control-lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            
            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  {t('ContactUs.Form.Email')}
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  placeholder={t('ContactUs.Form.EnterEmail')}
                  className="form-control-lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  {t('ContactUs.Form.PhoneNumber')}
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  placeholder={t('ContactUs.Form.EnterPhone')}
                  className="form-control-lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faHeading} className="me-2" />
                  {t('ContactUs.Form.Subject')}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  isInvalid={!!errors.subject}
                  placeholder={t('ContactUs.Form.EnterSubject')}
                  className="form-control-lg"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.subject}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>{t('ContactUs.Form.Message')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="message"
              value={formData.message}
              onChange={handleChange}
              isInvalid={!!errors.message}
              placeholder={t('ContactUs.Form.EnterMessage')}
              className="form-control-lg"
            />
            <Form.Control.Feedback type="invalid">
              {errors.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="text-center d-flex justify-content-center">
            <CTAPrimaryButton
              className=""
              text={isSubmitting ? t('ContactUs.Form.Sending') : t('ContactUs.Form.SendMessage')}
              icon={faPaperPlane}
              disabled={isSubmitting}
              onClick={handleSubmit}
            />
          </div>
        </Form>
      </div>

     
    </Container>
  );
};

export default ContactUs;
